import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../lib/format';
import { Button, Callout, Checkbox, ErrorState, Field, Input, Ltr, Select, Textarea, useConfirm, useDebounced, useToast, useUnsavedWarning } from '../components/ui';
import { IconTrash } from '../components/icons';
import { IssueOrder, type IssuedInfo } from '../components/IssueOrder';
import { PaymentRows, newPayment, type Payment } from '../components/PaymentRows';
import { MarginBadge } from '../components/Margin';
import { CostDialog, QuickProductDialog } from '../components/ProfitGaps';
import { useCan } from '../lib/auth';
import { marginOf, unitCost, type ProductCost } from '../lib/profit';

type DocType = 'quote' | 'delivery_note' | 'tax_invoice' | 'tax_invoice_receipt';
type PriceSource = 'manual' | 'last' | 'base' | 'typed' | null;
type Line = {
  key: string;
  productId: number | null;
  sku: string | null;
  description: string;
  quantity: string;
  unitPrice: string;
  basePrice: number | null;
  priceSource: PriceSource;
};
type OrderResponse = {
  order: {
    id: number;
    customerId: number;
    documentType: DocType;
    status: IssuedInfo['status'];
    morningNumber: string | null;
    morningUrl: string | null;
    issuedEnvironment: 'sandbox' | 'production' | null;
    emailedTo: string | null;
    documentId: number | null;
    issueError: string | null;
    lines: Array<{ productId: number | null; sku: string | null; description: string; quantity: number; unitPrice: number; basePrice: number | null; priceSource: PriceSource }>;
    addressId: number | null;
    dueDate: string | null;
    payments: Payment[];
    preparing: boolean;
    prepNote: string | null;
    notes: string | null;
    emailText: string | null;
    updatedAt: string;
    sourceDocumentId: number | null;
  };
  customer: { id: number; businessName: string; email: string | null; phone: string | null; termsLabel: string; rootId: number };
  family: Array<{ id: number; businessName: string; parentId: number | null }>;
  addresses: Array<{ id: number; customerId: number; label: string; text: string; isPrimary: boolean }>;
  vatRate: number;
  delivery: { label: string; price: number };
  links: {
    source: { id: number; type: string; typeLabel: string; number: string | null; inMorning: boolean } | null;
    billingNotes: Array<{ id: number; number: string | null; inMorning: boolean }>;
  };
  suggestedDueDate: string | null;
  invoiceDueDate: string | null;
};
type CatalogItem = { productId: number; sku: string; name: string; description: string | null; unit: string | null; price: number | null; basePrice: number | null; source: 'manual' | 'last' | 'base' | 'none'; lastDate: string | null };

const TYPES: Array<{ key: DocType; label: string }> = [
  { key: 'quote', label: 'הצעת מחיר' },
  { key: 'delivery_note', label: 'תעודת משלוח' },
  { key: 'tax_invoice', label: 'חשבונית מס' },
  { key: 'tax_invoice_receipt', label: 'חשבונית מס/קבלה' },
];
const SOURCE_LABEL: Record<string, string> = { manual: 'מחיר ידני ללקוח', last: 'המחיר האחרון שלו', base: 'מחיר בסיס', typed: 'הוקלד', none: 'אין מחיר' };

const num = (s: string) => {
  const n = Number(String(s).replace(/[₪,\s]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};
let keySeq = 0;
const newKey = () => `l${++keySeq}`;

export function OrderEditorPage() {
  const id = Number(useParams().id);
  const { data, error, refetch } = useQuery({ queryKey: ['order', id], queryFn: () => api<OrderResponse>(`/api/orders/${id}`), refetchOnWindowFocus: false });
  if (error) return <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  if (!data) return <div className="panel panel-body"><span className="spinner" /></div>;
  return <Editor key={data.order.id} initial={data} />;
}

function Editor({ initial }: { initial: OrderResponse }) {
  const o = initial.order;
  const [customerId, setCustomerId] = useState(o.customerId);
  const [docType, setDocType] = useState<DocType>(o.documentType);
  const [lines, setLines] = useState<Line[]>(() =>
    o.lines.map((l) => ({ key: newKey(), productId: l.productId, sku: l.sku, description: l.description, quantity: String(l.quantity), unitPrice: String(l.unitPrice), basePrice: l.basePrice, priceSource: l.priceSource })),
  );
  const [addressId, setAddressId] = useState<number | null>(o.addressId);
  const [dueDate, setDueDate] = useState<string | null>(o.dueDate ?? initial.suggestedDueDate);
  const [payments, setPayments] = useState<Payment[]>(o.payments ?? []);
  const [preparing, setPreparing] = useState(Boolean(o.preparing));
  const [prepNote, setPrepNote] = useState(o.prepNote ?? '');
  const [notes, setNotes] = useState(o.notes ?? '');
  const [emailText, setEmailText] = useState(o.emailText ?? '');
  const [save, setSave] = useState<{ state: 'saved' | 'dirty' | 'saving' | 'error'; at?: string; message?: string }>({ state: 'saved', at: o.updatedAt });
  /** the quantity / price input of a line (found by data attributes once the row is rendered) */
  const field = (key: string, f: 'qty' | 'price') => document.querySelector<HTMLInputElement>(`input[data-line="${key}"][data-field="${f}"]`);
  const focusField = (key: string, f: 'qty' | 'price') => {
    // the row may not be rendered yet — try on the next frames
    let tries = 0;
    const go = () => {
      const el = field(key, f);
      if (el) {
        el.focus();
        el.select();
      } else if (tries++ < 10) requestAnimationFrame(go);
    };
    requestAnimationFrame(go);
  };
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const readOnly = o.status !== 'draft';

  const vatRate = initial.vatRate;
  const totals = useMemo(() => {
    const subtotal = lines.reduce((s, l) => {
      const q = num(l.quantity);
      const p = num(l.unitPrice);
      return s + (Number.isFinite(q) && Number.isFinite(p) ? Math.round(q * p * 100) : 0);
    }, 0);
    const vat = Math.round(subtotal * vatRate);
    return { subtotal: subtotal / 100, vat: vat / 100, total: (subtotal + vat) / 100 };
  }, [lines, vatRate]);

  // ---- live margin (profit.view only; never printed or sent to Morning)
  const can = useCan();
  const showProfit = can('profit.view');
  const productIds = useMemo(() => [...new Set(lines.map((l) => l.productId).filter((x): x is number => x != null))].sort((a, b) => a - b), [lines]);
  const costs = useQuery({
    queryKey: ['profit', 'costs', productIds.join(',')],
    queryFn: () => api<{ items: Record<number, ProductCost>; target: number; warn: number }>(`/api/profit/costs${qs({ ids: productIds.join(',') })}`),
    enabled: showProfit && productIds.length > 0,
    placeholderData: (prev) => prev,
  });
  const target = costs.data?.target ?? 30;
  const warn = costs.data?.warn ?? 20;
  const lineProfit = (l: Line) => {
    const q = num(l.quantity);
    const p = num(l.unitPrice);
    if (!l.productId || !(q > 0) || !(p > 0)) return null;
    const uc = unitCost(p, costs.data?.items[l.productId]);
    if (uc == null) return null;
    return { ...marginOf(p, uc, target, warn), revenue: q * p, cost: q * uc };
  };
  const orderProfit = useMemo(() => {
    let revenue = 0;
    let cost = 0;
    let costed = 0;
    for (const l of lines) {
      const lp = lineProfit(l);
      if (!lp) continue;
      revenue += lp.revenue;
      cost += lp.cost;
      costed++;
    }
    return { ...marginOf(revenue, cost, target, warn), profit: revenue - cost, costed, coverage: totals.subtotal > 0 ? Math.round((revenue / totals.subtotal) * 100) : 0 };
  }, [lines, costs.data, totals.subtotal]);

  // One payment covers the whole document: it follows the total as lines change.
  useEffect(() => {
    if (docType === 'tax_invoice_receipt' && !readOnly && payments.length === 1 && Math.abs(payments[0]!.amount - totals.total) > 0.009) {
      setPayments([{ ...payments[0]!, amount: totals.total }]);
    }
  }, [totals.total, docType]);

  // ---- the delivery line, one tick
  const isDelivery = (l: Line) => !l.productId && l.description.trim() === initial.delivery.label.trim();
  const hasDelivery = lines.some(isDelivery);
  const toggleDelivery = (on: boolean) =>
    setLines((ls) =>
      on
        ? [...ls, { key: newKey(), productId: null, sku: null, description: initial.delivery.label, quantity: '1', unitPrice: String(initial.delivery.price), basePrice: null, priceSource: 'typed' }]
        : ls.filter((l) => !isDelivery(l)),
    );

  const invalid = lines.some((l) => !(num(l.quantity) > 0) || !(num(l.unitPrice) >= 0) || !l.description.trim());

  // ---- autosave: every change is saved shortly after typing stops
  const payload = useMemo(
    () => ({
      customerId,
      documentType: docType,
      lines: lines.map((l) => ({ productId: l.productId, sku: l.sku, description: l.description.trim(), quantity: num(l.quantity), unitPrice: num(l.unitPrice), basePrice: l.basePrice, priceSource: l.priceSource })),
      addressId,
      dueDate: docType === 'tax_invoice' ? dueDate : null,
      payments: docType === 'tax_invoice_receipt' ? payments : [],
      preparing,
      prepNote: prepNote.trim() || null,
      notes: notes.trim() || null,
      emailText: emailText.trim() || null,
    }),
    [customerId, docType, lines, addressId, dueDate, notes, emailText, payments, preparing, prepNote],
  );
  const debounced = useDebounced(payload, 700);
  // Compared with what the server last confirmed, so opening a draft never counts as a change.
  const lastSaved = useRef(JSON.stringify(payload));
  const changed = JSON.stringify(payload) !== lastSaved.current;
  useUnsavedWarning(changed);
  useEffect(() => {
    if (changed) setSave((s) => (s.state === 'saving' ? s : { state: 'dirty' }));
  }, [changed, payload]);
  useEffect(() => {
    const body = JSON.stringify(debounced);
    if (readOnly || invalid || body === lastSaved.current) return;
    let cancelled = false;
    setSave({ state: 'saving' });
    api<{ savedAt: string }>(`/api/orders/${o.id}`, { method: 'PUT', body: debounced })
      .then((r) => {
        lastSaved.current = body;
        if (!cancelled) setSave({ state: 'saved', at: r.savedAt });
      })
      .catch((e: Error) => !cancelled && setSave({ state: 'error', message: e.message }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  // ---- lines
  const focusQty = (key: string) => focusField(key, 'qty');
  const addProduct = (p: CatalogItem) => {
    const existing = lines.find((l) => l.productId === p.productId);
    if (existing) {
      toast.success('המוצר כבר בהזמנה — עדכנו את הכמות');
      return focusQty(existing.key);
    }
    const key = newKey();
    setLines((ls) => [
      ...ls,
      {
        key,
        productId: p.productId,
        sku: p.sku,
        description: p.description ? `${p.name} — ${p.description}` : p.name,
        quantity: '1',
        unitPrice: p.price == null ? '' : String(p.price),
        basePrice: p.basePrice,
        priceSource: p.source === 'none' ? null : p.source,
      },
    ]);
    if (p.price == null) focusField(key, 'price');
    else focusQty(key);
  };
  const addFree = (text: string) => {
    const key = newKey();
    setLines((ls) => [...ls, { key, productId: null, sku: null, description: text, quantity: '1', unitPrice: '', basePrice: null, priceSource: 'typed' }]);
    focusField(key, 'price');
  };
  const update = (key: string, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const remove = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key));
  // a free line → a catalog product (the line is linked to it); a product line without a cost → its cost
  const [toCatalog, setToCatalog] = useState<Line | null>(null);
  const [costFor, setCostFor] = useState<{ id: number; name: string } | null>(null);
  const backToSearch = useCallback(() => document.querySelector<HTMLInputElement>('input[data-order-search]')?.focus(), []);

  /** Saves the current lines right away (before a preview). */
  const saveNow = async () => {
    if (invalid) {
      toast.error(new Error('יש שורה בלי כמות, מחיר או תיאור'));
      return false;
    }
    const body = JSON.stringify(payload);
    if (body === lastSaved.current) return true;
    try {
      const r = await api<{ savedAt: string }>(`/api/orders/${o.id}`, { method: 'PUT', body: payload });
      lastSaved.current = body;
      setSave({ state: 'saved', at: r.savedAt });
      return true;
    } catch (e) {
      toast.error(e);
      return false;
    }
  };

  const removeDraft = async () => {
    if (!(await confirm({ title: 'למחוק את הטיוטה?', body: 'השורות שהוקלדו יימחקו. אי אפשר לשחזר.', danger: true, confirmLabel: 'מחיקה' }))) return;
    try {
      await api(`/api/orders/${o.id}`, { method: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['orders'] });
      toast.success('הטיוטה נמחקה');
      navigate(`/customers/${initial.customer.rootId}?tab=orders`);
    } catch (e) {
      toast.error(e);
    }
  };

  const familyAddresses = initial.addresses.filter((a) => a.customerId === customerId || a.customerId === initial.customer.rootId);
  const selectedCustomer = initial.family.find((f) => f.id === customerId);

  return (
    <>
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          <div className="crumbs">
            <Link to={`/customers/${initial.customer.rootId}?tab=orders`}>{initial.family.find((f) => f.id === initial.customer.rootId)?.businessName ?? initial.customer.businessName}</Link>
            {' / '}הזמנות
          </div>
          <h1>{o.status === 'issued' ? `${TYPES.find((t) => t.key === docType)!.label} ${o.morningNumber ?? ''}` : readOnly ? 'הזמנה' : 'הזמנה חדשה'} — {selectedCustomer?.businessName}</h1>
          <div className="sub row wrap" style={{ gap: 12 }}>
            <SaveState save={save} invalid={invalid} readOnly={readOnly} />
            <span>{initial.customer.termsLabel}</span>
            {initial.customer.email ? <bdi>{initial.customer.email}</bdi> : <span style={{ color: 'var(--warn)' }}>אין מייל ללקוח</span>}
          </div>
        </div>
        <div className="row wrap">
          <div className="seg" role="radiogroup" aria-label="סוג מסמך">
            {TYPES.map((t) => (
              <button key={t.key} type="button" role="radio" aria-checked={docType === t.key} className={`seg-btn ${docType === t.key ? 'active' : ''}`} disabled={readOnly} onClick={() => {
                setDocType(t.key);
                if (t.key === 'tax_invoice' && !dueDate) setDueDate(initial.invoiceDueDate);
                // paid now: one payment for the whole document, by bank transfer unless changed
                if (t.key === 'tax_invoice_receipt' && !payments.length) setPayments([newPayment(totals.total)]);
              }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {lines.length ? (
        <div className="row no-print" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
          <Link to={`/pick/order/${o.id}`} className="btn sm" title="מסך ליקוט נוח למחשב ולטלפון — מסמנים כל מוצר שלוקט">מסך ליקוט</Link>
        </div>
      ) : null}
            <OrderLinks links={initial.links} docType={docType} />

      <div className="order-grid">
        <section className="panel">
          <div className="table-wrap">
            <table className="table order-lines">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>#</th>
                  <th>מוצר</th>
                  <th className="num" style={{ width: 96 }}>כמות</th>
                  <th className="num" style={{ width: 130 }}>מחיר ליח׳ (לפני מע״מ)</th>
                  <th className="num" style={{ width: 110 }}>סה״כ</th>
                  {showProfit ? <th className="num no-print" style={{ width: 80 }} title="(מחיר − עלות) ÷ מחיר · לא מופיע במסמך">רווחיות</th> : null}
                  <th style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const q = num(l.quantity);
                  const p = num(l.unitPrice);
                  const below = l.basePrice != null && Number.isFinite(p) && p < l.basePrice;
                  const lp = showProfit ? lineProfit(l) : null;
                  return (
                    <tr key={l.key}>
                      <td className="muted tabular">{i + 1}</td>
                      <td>
                        {l.productId ? (
                          <span className="strong">{l.description}</span>
                        ) : (
                          <Input value={l.description} onChange={(e) => update(l.key, { description: e.target.value })} disabled={readOnly} aria-label="תיאור" />
                        )}
                        <span className="sub">
                          {l.sku ? <Ltr>{l.sku}</Ltr> : 'שורה חופשית'}
                          {l.priceSource ? ` · ${SOURCE_LABEL[l.priceSource]}` : ''}
                          {!readOnly && !l.productId && !isDelivery(l) && l.description.trim() && can('catalog.edit') ? (
                            <>
                              {' · '}
                              <button type="button" className="link-button no-print" onClick={() => setToCatalog(l)} title="המוצר נכנס לקטלוג עם מחיר עלות, והשורה משויכת אליו">+ לקטלוג</button>
                            </>
                          ) : null}
                          {showProfit && l.productId && costs.data && costs.data.items[l.productId]?.cost == null ? (
                            <>
                              {' · '}
                              <button type="button" className="link-button no-print" onClick={() => setCostFor({ id: l.productId!, name: l.description })}>הזנת עלות</button>
                            </>
                          ) : null}
                          {below ? <span style={{ color: 'var(--warn)' }}> · מתחת למחיר הבסיס ({fmtMoney(l.basePrice)})</span> : null}
                          {lp?.level === 'loss' ? <span className="no-print" style={{ color: 'var(--bad)' }}> · מתחת לעלות!</span> : lp?.level === 'weak' || lp?.level === 'warn' ? <span className="no-print" style={{ color: 'var(--warn)' }}> · מתחת ליעד הרווחיות ({target}%)</span> : null}
                        </span>
                      </td>
                      <td className="num">
                        <Input
                          data-line={l.key}
                          data-field="qty"
                          className="qty-input"
                          inputMode="decimal"
                          value={l.quantity}
                          disabled={readOnly}
                          aria-invalid={!(q > 0)}
                          onChange={(e) => update(l.key, { quantity: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), backToSearch())}
                          aria-label="כמות"
                        />
                      </td>
                      <td className="num">
                        <Input
                          data-line={l.key}
                          data-field="price"
                          className="price-input"
                          inputMode="decimal"
                          value={l.unitPrice}
                          disabled={readOnly}
                          aria-invalid={!(p >= 0) || l.unitPrice === ''}
                          style={below ? { borderColor: 'var(--warn)' } : undefined}
                          onChange={(e) => update(l.key, { unitPrice: e.target.value, priceSource: l.productId ? 'typed' : l.priceSource })}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), focusQty(l.key))}
                          aria-label="מחיר ליחידה"
                        />
                      </td>
                      <td className="num tabular">{Number.isFinite(q) && Number.isFinite(p) ? fmtMoney(Math.round(q * p * 100) / 100) : '—'}</td>
                      {showProfit ? (
                        <td className="num no-print">
                          {lp ? <MarginBadge margin={lp.margin} level={lp.level} title={`רווח ${fmtMoney(Math.round((lp.revenue - lp.cost) * 100) / 100)} בשורה`} /> : <span className="faint" title={l.productId ? 'אין עלות למוצר' : 'שורה חופשית'}>—</span>}
                        </td>
                      ) : null}
                      <td>
                        {!readOnly ? (
                          <Button size="sm" variant="ghost" icon onClick={() => remove(l.key)} aria-label="הסרת שורה" title="הסרת שורה">
                            <IconTrash size={14} />
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {!readOnly ? (
                  <tr className="order-entry">
                    <td className="muted tabular">{lines.length + 1}</td>
                    <td colSpan={showProfit ? 6 : 5}>
                      <LineSearch customerId={customerId} onPick={addProduct} onFree={addFree} />
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {lines.length === 0 ? (
            <div className="panel-body small muted">
              מקלידים כמה אותיות מהשם או מק״ט, Enter מוסיף את המוצר במחיר של הלקוח, מקלידים כמות ושוב Enter — חוזרים לשורה הבאה.
            </div>
          ) : null}
        </section>

        <aside className="stack" style={{ gap: 12 }}>
          {!readOnly ? (
            <section className="panel panel-body stack" style={{ gap: 6, ...(preparing ? { borderColor: 'var(--warn)', background: 'var(--warn-soft)' } : {}) }}>
              <Checkbox checked={preparing} onChange={setPreparing}>
                <strong>בהכנה</strong> — להוסיף להזמנות לאספקה עוד לפני שמפיקים מסמך
              </Checkbox>
              {preparing ? (
                <Input value={prepNote} onChange={(e) => setPrepNote(e.target.value)} placeholder="מה חסר / הערה להכנה — למשל: חסר במלאי טונר, הלקוח יוסיף מוצרים" aria-label="הערה להכנה" />
              ) : (
                <span className="small muted">למשל כשחסר משהו במלאי או שהלקוח עוד מוסיף מוצרים — כדי שההזמנה לא תישכח.</span>
              )}
            </section>
          ) : null}
          <section className="panel panel-body order-totals">
            <Checkbox checked={hasDelivery} disabled={readOnly} onChange={toggleDelivery}>
              {initial.delivery.label} — {fmtMoney(initial.delivery.price)} לפני מע״מ
            </Checkbox>
            <div className="row"><span>{fmtNumber(lines.length)} שורות · לפני מע״מ</span><span className="spacer" /><span className="tabular">{fmtMoney(totals.subtotal)}</span></div>
            <div className="row"><span>מע״מ {Math.round(vatRate * 100)}%</span><span className="spacer" /><span className="tabular">{fmtMoney(totals.vat)}</span></div>
            <div className="row strong" style={{ fontSize: 18 }}><span>סה״כ לתשלום</span><span className="spacer" /><span className="tabular">{fmtMoney(totals.total)}</span></div>
            {showProfit && orderProfit.costed ? (
              <div className="no-print stack" style={{ gap: 6, borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 2 }}>
                <div className="row">
                  <span>רווחיות ההזמנה</span>
                  <span className="spacer" />
                  <MarginBadge margin={orderProfit.margin} level={orderProfit.level} />
                </div>
                <div className="small muted">
                  רווח גולמי {fmtMoney(Math.round(orderProfit.profit * 100) / 100)}
                  {orderProfit.coverage < 100 ? ` · לפי ${orderProfit.coverage}% מהסכום (שורות עם עלות ידועה)` : ''} · יעד {target}% · לא מופיע במסמך
                </div>
                {orderProfit.level === 'loss' ? <Callout tone="bad">ההזמנה מתחת לעלות.</Callout> : orderProfit.level && orderProfit.level !== 'good' ? <Callout tone="warn">ההזמנה מתחת ליעד הרווחיות ({target}%).</Callout> : null}
              </div>
            ) : null}
          </section>

          <section className="panel panel-body stack" style={{ gap: 10 }}>
            {initial.family.length > 1 ? (
              <Field label="לקוח / סניף">
                <Select value={customerId} disabled={readOnly} onChange={(e) => setCustomerId(Number(e.target.value))}>
                  {initial.family.map((f) => <option key={f.id} value={f.id}>{f.parentId ? `↳ ${f.businessName}` : f.businessName}</option>)}
                </Select>
              </Field>
            ) : null}
            <Field label={docType === 'delivery_note' ? 'כתובת אספקה' : 'כתובת'}>
              <Select value={addressId ?? ''} disabled={readOnly} onChange={(e) => setAddressId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">ללא</option>
                {familyAddresses.map((a) => <option key={a.id} value={a.id}>{a.label}{a.text ? ` — ${a.text}` : ''}</option>)}
              </Select>
            </Field>
            {docType === 'tax_invoice' ? (
              <Field label="לתשלום עד" hint={`לפי תנאי הלקוח: ${initial.customer.termsLabel}`}>
                <Input type="date" value={dueDate ?? ''} disabled={readOnly} onChange={(e) => setDueDate(e.target.value || null)} ltr />
              </Field>
            ) : null}
            {docType === 'tax_invoice_receipt' ? (
              <Field label="תשלום" hint="חשבונית מס/קבלה — המסמך נרשם כשולם">
                <PaymentRows payments={payments} total={totals.total} onChange={setPayments} disabled={readOnly} />
              </Field>
            ) : null}
            <Field label="הערות במסמך">
              <Textarea rows={2} value={notes} disabled={readOnly} onChange={(e) => setNotes(e.target.value)} />
            </Field>
            <Field label="טקסט למייל ללקוח" hint="יישלח עם המסמך כשההפקה במורנינג תופעל">
              <Textarea rows={2} value={emailText} disabled={readOnly} onChange={(e) => setEmailText(e.target.value)} />
            </Field>
          </section>

          <IssueOrder
            orderId={o.id}
            docType={docType}
            total={totals.total}
            customer={{ email: initial.customer.email, phone: initial.customer.phone, businessName: selectedCustomer?.businessName ?? initial.customer.businessName }}
            saveNow={saveNow}
            info={{ status: o.status, morningNumber: o.morningNumber, morningUrl: o.morningUrl, issuedEnvironment: o.issuedEnvironment, emailedTo: o.emailedTo, documentId: o.documentId, issueError: o.issueError }}
            onIssued={() => qc.invalidateQueries({ queryKey: ['order', o.id] })}
          />
          {!readOnly ? (
            <section className="panel panel-body stack" style={{ gap: 8 }}>
              <Callout tone="info">הטיוטה נשמרת אוטומטית. אפשר לחזור אליה מכרטיס הלקוח (לשונית "הזמנות") או מהמסך "הזמנות".</Callout>
              <Button variant="danger" onClick={removeDraft}>
                <IconTrash /> מחיקת הטיוטה
              </Button>
            </section>
          ) : null}
        </aside>
      </div>
      {toCatalog ? (
        <QuickProductDialog
          text={toCatalog.description.trim()}
          price={Number.isFinite(num(toCatalog.unitPrice)) && toCatalog.unitPrice !== '' ? num(toCatalog.unitPrice) : null}
          canCost={showProfit}
          onClose={() => setToCatalog(null)}
          onCreated={(p) => {
            update(toCatalog.key, { productId: p.id, sku: p.sku, description: p.name, basePrice: p.price });
            setToCatalog(null);
          }}
        />
      ) : null}
      <CostDialog
        product={costFor}
        onClose={() => setCostFor(null)}
        onSaved={() => {
          setCostFor(null);
          qc.invalidateQueries({ queryKey: ['profit'] });
        }}
      />
    </>
  );
}

function SaveState({ save, invalid, readOnly }: { save: { state: string; at?: string; message?: string }; invalid: boolean; readOnly: boolean }) {
  if (readOnly) return null;
  if (invalid && save.state !== 'saved') return <span style={{ color: 'var(--warn)' }}>יש שורה בלי כמות / מחיר — תישמר כשתושלם</span>;
  if (save.state === 'saving') return <span className="muted">שומר…</span>;
  if (save.state === 'dirty') return <span className="muted">ממתין לשמירה…</span>;
  if (save.state === 'error') return <span style={{ color: 'var(--bad)' }}>לא נשמר: {save.message}</span>;
  return <span style={{ color: 'var(--ok)' }}>✓ נשמר{save.at ? ` ${new Date(save.at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}` : ''}</span>;
}

/** Type letters or a SKU → products at this customer's price. Enter adds; no match → a free line. */
function LineSearch({ customerId, onPick, onFree }: { customerId: number; onPick: (p: CatalogItem) => void; onFree: (text: string) => void }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const dq = useDebounced(q.trim(), 150);
  const { data, isFetching } = useQuery({
    queryKey: ['order-catalog', customerId, dq],
    queryFn: () => api<{ items: CatalogItem[] }>(`/api/orders/catalog/${customerId}${qs({ q: dq })}`),
    enabled: dq.length >= 2,
    staleTime: 60_000,
  });
  const items = dq.length >= 2 ? data?.items ?? [] : [];
  const options = q.trim().length >= 2 ? [...items.map((i) => ({ kind: 'product' as const, item: i })), { kind: 'free' as const, text: q.trim() }] : [];
  useEffect(() => setActive(0), [dq]);
  useEffect(() => document.querySelector<HTMLInputElement>('input[data-order-search]')?.focus(), []);
  const choose = (i: number) => {
    const opt = options[i];
    if (!opt) return;
    if (opt.kind === 'product') onPick(opt.item);
    else onFree(opt.text);
    setQ('');
    setOpen(false);
  };
  return (
    <div className="picker" style={{ position: 'relative' }}>
      <Input
        data-order-search
        value={q}
        placeholder="הוספת מוצר — אותיות מהשם, צבע / גודל או מק״ט…"
        aria-label="הוספת מוצר"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(options.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (dq !== q.trim() || isFetching) return; // wait for the results of what was typed
            choose(active);
          } else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && options.length ? (
        <div className="picker-menu" role="listbox" style={{ maxHeight: 380 }}>
          {options.map((opt, i) =>
            opt.kind === 'product' ? (
              <button key={opt.item.productId} type="button" role="option" aria-selected={i === active} className={`picker-item ${i === active ? 'active' : ''}`} onMouseEnter={() => setActive(i)} onMouseDown={(e) => (e.preventDefault(), choose(i))}>
                <span className="ellipsis" style={{ flex: 1 }}>
                  {opt.item.name}
                  {opt.item.description ? <span className="muted"> — {opt.item.description}</span> : null}
                </span>
                <Ltr className="small muted tabular">{opt.item.sku}</Ltr>
                <span className="small tabular" style={{ minWidth: 76, textAlign: 'left' }} title={SOURCE_LABEL[opt.item.source] + (opt.item.lastDate ? ` · ${fmtDate(opt.item.lastDate)}` : '')}>
                  {opt.item.price == null ? <span className="faint">אין מחיר</span> : (
                    <span className={opt.item.source === 'manual' || opt.item.source === 'last' ? 'strong' : undefined}>{fmtMoney(opt.item.price)}</span>
                  )}
                </span>
              </button>
            ) : (
              <button key="free" type="button" role="option" aria-selected={i === active} className={`picker-item ${i === active ? 'active' : ''}`} onMouseEnter={() => setActive(i)} onMouseDown={(e) => (e.preventDefault(), choose(i))}>
                <span className="muted">+ שורה חופשית: </span>
                <span className="ellipsis" style={{ flex: 1 }}>"{opt.text}"</span>
              </button>
            ),
          )}
          {isFetching && !items.length ? <div className="picker-empty">מחפש…</div> : null}
        </div>
      ) : null}
    </div>
  );
}

/** What issuing this order also does to the documents it comes from. */
function OrderLinks({ links, docType }: { links: OrderResponse['links']; docType: DocType }) {
  const { source, billingNotes } = links;
  if (billingNotes.length) {
    return (
      <Callout tone="info" title={`חשבונית מרכזת ל-${billingNotes.length} תעודות משלוח`}>
        אחרי ההפקה התעודות ({billingNotes.map((n) => n.number).filter(Boolean).join(', ')}) יסומנו כמחויבות ויצאו ממסך "חיוב סוף חודש"
        {billingNotes.some((n) => n.inMorning) ? ', ובמורנינג החשבונית תקושר אליהן' : ''}. שורה שתימחק כאן — התעודה שלה לא תחויב.
      </Callout>
    );
  }
  if (source && source.type === 'quote' && docType !== 'quote') {
    return (
      <Callout tone="info" title={`מימוש הצעת מחיר ${source.number ?? ''}`}>
        אחרי ההפקה ההצעה תסומן כמאושרת{source.inMorning ? ' ובמורנינג המסמך יקושר להצעה' : ''}.
      </Callout>
    );
  }
  return null;
}
